import { forwardRef, useState } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import AppBar from '@zenbusiness/zui/AppBar';
import Button from '@zenbusiness/zui/Button';
import Drawer from '@zenbusiness/zui/Drawer';
import Grid from '@zenbusiness/zui/Grid';
import Hidden from '@zenbusiness/zui/Hidden';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import ExternalLink from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import Toolbar from '@zenbusiness/zui/Toolbar';
import PropTypes from 'prop-types';

import CloseIcon from '@mui/icons-material/Close';

import FlexColumn from '#client/components/FlexColumn';
import FlexRow from '#client/components/FlexRow';
import Footer from '#client/components/Footer';

const DRAWER_WIDTH = 240;

const TemplateAppBar = styled(AppBar, {
  name: 'Template',
  slot: 'AppBar'
})(({ theme }) => ({
  zIndex: (theme.zIndex?.drawer || 0) + 100,
  borderBottom: '2px solid #CCCCCC'
}));

const TemplateToolbar = styled(Toolbar, {
  name: 'Template',
  slot: 'Toolbar'
})({
  minHeight: 60
});

const TemplateDrawer = styled(Drawer, {
  name: 'Template',
  slot: 'Drawer'
})({
  borderTop: '2px solid #CCCCCC',
  width: DRAWER_WIDTH,
  flexShrink: 0
});

const TemplateMenuButton = styled(Button, {
  name: 'Template',
  slot: 'MenuButton'
})(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    display: 'none'
  }
}));

const TemplateContent = styled('main', {
  name: 'Template',
  slot: 'Content'
})({
  flexGrow: 1,
  width: '100%',
  padding: '24px 8px',
  maxWidth: '1280px',
  minHeight: 'calc(100vh - 32px)', // minus footer
  paddingTop: 0
});

const TemplateLogo = styled(SharedAsset, {
  name: 'Template',
  slot: 'Logo'
})({
  display: 'block',
  margin: '0 auto',
  height: '40px',
  width: '40px'
});

const TemplateOffset = styled('div', {
  name: 'Template',
  slot: 'Offset'
})(({ theme }) => theme.mixins.toolbar);

const TemplateOBO = styled('div', {
  name: 'Template',
  slot: 'OBO'
})(({ theme }) => ({
  color: theme.palette.text.contrast,
  backgroundColor: theme.palette.secondary.main,
  position: 'absolute',
  width: '100%',
  top: theme.mixins.toolbar.minHeight + 4,
  padding: '0 8px',
  zIndex: 'auto',
  [theme.breakpoints.up('sm')]: {
    top: theme.mixins.toolbar.minHeight + 12
  }
}));

const Template = forwardRef(function Template(
  { children, toolbarLeft, toolbarRight, navigation, loading },
  ref
) {
  const { authenticated, obo } = useAuthentication();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const isObo = !!obo;

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const drawer = (
    <Grid
      container
      direction="column"
      justify="space-between"
      sx={{ minHeight: '100vh' }}
      wrap="nowrap"
    >
      <Grid item>
        <TemplateOffset />
        {navigation}
      </Grid>
    </Grid>
  );

  return (
    <FlexColumn
      fill
      ref={ref}
    >
      <TemplateAppBar
        color="inherit"
        component="header"
        position="fixed"
      >
        {isObo && (
          <TemplateOBO>
            <Text
              align="center"
              size="sm"
            >
              {`OBO: ${obo}`}
            </Text>
          </TemplateOBO>
        )}
        <Toolbar>
          <Grid
            alignItems="center"
            container
            direction="row"
            justify="space-between"
          >
            <Grid
              alignItems="center"
              container
              direction="row"
              item
              justify="space-between"
              xs
            >
              {authenticated && navigation && (
                <TemplateMenuButton
                  aria-label="open drawer"
                  color={!drawerOpen ? 'primary' : 'inherit'}
                  edge="start"
                  onClick={handleDrawerToggle}
                  size={!drawerOpen ? 'small' : 'medium'}
                >
                  {!drawerOpen ? 'Manage' : <CloseIcon />}
                </TemplateMenuButton>
              )}
              {authenticated && toolbarLeft && <Hidden xsDown>{toolbarLeft}</Hidden>}
            </Grid>
            <Grid
              item
              xs
            >
              <ExternalLink
                href="/dashboard"
                sx={{ flexGrow: 1 }}
              >
                <FlexRow
                  justify="center"
                  sx={{ margin: '40px' }}
                >
                  <TemplateLogo
                    alt="ZenBusiness Logo"
                    src="/logo/new-circle-logo-teal.svg"
                    width={40}
                  />
                </FlexRow>
              </ExternalLink>
            </Grid>
            <Grid
              align="right"
              item
              xs
            >
              {authenticated && toolbarRight}
            </Grid>
          </Grid>
        </Toolbar>
        {!!loading && <LinearProgress />}
      </TemplateAppBar>
      <FlexRow
        fill
        justify="center"
      >
        {authenticated && navigation && (
          <nav>
            <Hidden lgUp>
              <TemplateDrawer
                anchor="left"
                ModalProps={{
                  keepMounted: true // Better open performance on mobile.
                }}
                onClose={handleDrawerToggle}
                open={drawerOpen}
                variant="temporary"
              >
                {drawer}
              </TemplateDrawer>
            </Hidden>
            <Hidden mdDown>
              <TemplateDrawer variant="permanent">{drawer}</TemplateDrawer>
            </Hidden>
          </nav>
        )}
        <TemplateContent>
          <TemplateToolbar />
          {children}
        </TemplateContent>
      </FlexRow>
      <Footer />
      {/* <ScrollTo /> */}
    </FlexColumn>
  );
});

Template.propTypes = {
  children: PropTypes.node,
  toolbarLeft: PropTypes.node,
  toolbarRight: PropTypes.node,
  navigation: PropTypes.node,
  loading: PropTypes.bool
};

export default Template;
